import Inbox from "@/pages/Inbox";
import ChatThread from "@/pages/ChatThread";
import BuddyReads from "@/pages/BuddyReads";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { AchievementNotificationDisplay } from "@/components/AchievementNotificationDisplay";
import { CookieConsent } from "@/components/CookieConsent";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "next-themes";
import { useThemeSchedule } from "@/hooks/useThemeSchedule";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { MobileNavigation } from "@/components/MobileNavigation";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { Header } from "@/components/Header";
import { AuthCheck } from "@/components/AuthCheck";
import { SafeModeGuard } from "@/components/SafeModeGuard";
import { EmailVerificationGuard } from "@/components/EmailVerificationGuard";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import Home from "./pages/Home";
import Auth from "./pages/Auth";
import Search from "./pages/Search";
import BookDetail from "./pages/BookDetail";
import Import from "./pages/Import";
import OnboardingImport from "./pages/OnboardingImport";
import OnboardingGenres from "./pages/OnboardingGenres";
import OnboardingReadingGoal from "./pages/OnboardingReadingGoal";
import PublicProfile from "./pages/PublicProfile";
import LeaderboardPage from "./pages/LeaderboardPage";
import Wrapped from "./pages/Wrapped";
import Settings from "./pages/Settings";
import Preferences from "./pages/Preferences";
import NotFound from "./pages/NotFound";
import OnboardingHub from "./pages/OnboardingHub";
import OnboardingProfile from "./pages/OnboardingProfile";
import OnboardingArchetype from "./pages/OnboardingArchetype";
import OnboardingReadingFormat from "./pages/OnboardingReadingFormat";
import OnboardingResults from "./pages/OnboardingResults";
import Onboarding from "./pages/Onboarding";
import OnboardingBookshelfScan from "./pages/OnboardingBookshelfScan";
import HealthCheck from "./pages/HealthCheck";
import BackgroundRemovalTool from "./pages/BackgroundRemovalTool";
import FixMascots from "./pages/FixMascots";
import AdminEnrichment from "./pages/AdminEnrichment";
import AdminModeration from "./pages/AdminModeration";
import AdminAwards from "./pages/AdminAwards";
import AdminDataCleanup from "./pages/AdminDataCleanup";
import AdminBookImport from "./pages/AdminBookImport";
import CommunityGuidelines from "./pages/CommunityGuidelines";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Focus from "./pages/Focus";
import Achievements from "./pages/Achievements";
import Leaderboards from "./pages/Leaderboards";
import Discover from "./pages/Discover";
import BuddyRead from "./pages/BuddyRead";
import Awards from "./pages/Awards";
import AwardDetail from "./pages/AwardDetail";
import AwardMatches from "./pages/AwardMatches";
import TagDiscovery from "./pages/TagDiscovery";
import RecommendationHistory from "./pages/RecommendationHistory";
import SeriesTracker from "./pages/SeriesTracker";
import AuthorPage from "./pages/AuthorPage";
import { Footer } from "@/components/Footer";
import { QuickActionFAB } from "@/components/QuickActionFAB";

const queryClient = new QueryClient();

function RoutedContent() {
  useKeyboardShortcuts();
  const location = useLocation();
  const isOnboardingOrAuth = location.pathname.startsWith('/onboarding') || location.pathname === '/auth';
  
  // Initialize theme schedule for logged-in users
  useThemeSchedule();
  
  return (
    <AuthCheck>
      <EmailVerificationGuard>
        <SidebarProvider defaultOpen={true}>
          <div className="min-h-screen w-full flex flex-col" style={{ ["--header-height" as any]: '4rem' }}>
            <a href="#main-content" className="skip-to-content">
              Skip to main content
            </a>
            <Header />
            <div className="flex flex-1 w-full">
              {!isOnboardingOrAuth && <AppSidebar />}
              <div className="flex-1 flex flex-col">
                <main id="main-content" className="flex-1 mobile-nav-padding" role="main">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="/discover" element={<Discover />} />
                    <Route path="/recommendations/history" element={<RecommendationHistory />} />
                    <Route path="/tags" element={<TagDiscovery />} />
                    <Route path="/series-tracker" element={<SeriesTracker />} />
                    <Route path="/book/:id" element={<BookDetail />} />
                    <Route path="/focus" element={<Focus />} />
                    <Route path="/achievements" element={<Achievements />} />
                    <Route path="/author/:authorName" element={<AuthorPage />} />
                    <Route path="/leaderboards" element={<Leaderboards />} />
                    <Route path="/awards" element={<Awards />} />
                    <Route path="/awards/:id" element={<AwardDetail />} />
                    <Route path="/awards/matches" element={<AwardMatches />} />
                    <Route path="/buddy-read/:id" element={<BuddyRead />} />
                    <Route path="/import" element={<Import />} />
                    <Route path="/u/:username" element={<PublicProfile />} />
                    <Route path="/u/:username/leaderboard" element={<LeaderboardPage />} />
                    <Route path="/wrapped" element={<Wrapped />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/profile" element={<Settings />} />
                    <Route path="/settings/preferences" element={<Preferences />} />
                    <Route path="/inbox" element={<Inbox />} />
                    <Route path="/chat/:chatId" element={<ChatThread />} />
                    <Route path="/buddy-reads" element={<BuddyReads />} />
                    <Route path="/healthz" element={<HealthCheck />} />
                    <Route path="/bg-removal" element={<BackgroundRemovalTool />} />
                    <Route path="/fix-mascots" element={<FixMascots />} />
                    <Route path="/admin/enrichment" element={<AdminEnrichment />} />
                    <Route path="/admin/moderation" element={<AdminModeration />} />
                    <Route path="/admin/awards" element={<AdminAwards />} />
                    <Route path="/admin/data-cleanup" element={<AdminDataCleanup />} />
                    <Route path="/admin/book-import" element={<AdminBookImport />} />
                    <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    {/* Onboarding routes with Safe Mode guards */}
                    <Route path="/onboarding" element={
                      <SafeModeGuard feature="onboarding">
                        <Onboarding />
                      </SafeModeGuard>
                    } />
                    <Route path="/onboarding/profile" element={
                      <SafeModeGuard feature="profile setup">
                        <OnboardingProfile />
                      </SafeModeGuard>
                    } />
                    <Route path="/onboarding/import" element={
                      <SafeModeGuard feature="data import">
                        <OnboardingImport />
                      </SafeModeGuard>
                    } />
                    <Route path="/onboarding/bookshelf-scan" element={<OnboardingBookshelfScan />} />
                    <Route path="/onboarding/genres" element={
                      <SafeModeGuard feature="genre selection">
                        <OnboardingGenres />
                      </SafeModeGuard>
                    } />
                    <Route path="/onboarding/reading-goal" element={
                      <SafeModeGuard feature="reading goal">
                        <OnboardingReadingGoal />
                      </SafeModeGuard>
                    } />
                    <Route path="/onboarding/reading-format" element={
                      <SafeModeGuard feature="reading format">
                        <OnboardingReadingFormat />
                      </SafeModeGuard>
                    } />
                    <Route path="/onboarding/archetype" element={
                      <SafeModeGuard feature="archetype quiz">
                        <OnboardingArchetype />
                      </SafeModeGuard>
                    } />
                    <Route path="/onboarding/results" element={
                      <SafeModeGuard feature="onboarding results">
                        <OnboardingResults />
                      </SafeModeGuard>
                    } />
                    {/* Catch-all route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            </div>
          </div>
          <MobileNavigation />
          <QuickActionFAB />
        </SidebarProvider>
      </EmailVerificationGuard>
    </AuthCheck>
  );
}

function AppContent() {
  return (
    <AppErrorBoundary>
      <Toaster />
      <Sonner />
      <AchievementNotificationDisplay />
      <CookieConsent />
      <GoogleAnalytics />
      <BrowserRouter>
        <RoutedContent />
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <TooltipProvider>
          <AppContent />
        </TooltipProvider>
      </ThemeProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;